import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, DollarSign, TrendingUp, ArrowRight, CheckCircle2, Package, Percent } from 'lucide-react';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';

export default function ProfitMarginCalculator() {
  const [costPrice, setCostPrice] = useState('');
  const [sellPrice, setSellPrice] = useState('');
  const [unitsSold, setUnitsSold] = useState('');
  const [period, setPeriod] = useState('month');

  // SEO: Set page title
  React.useEffect(() => {
    document.title = 'Smoke Shop Profit Margin Calculator (2025) | FlowFeed POS';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Free profit margin and markup calculator for smoke shops and convenience stores. See industry benchmarks and projected earnings. No signup required.');
    return () => { document.title = 'FlowFeed POS | #1 Point of Sale for Smoke Shops, C-Stores & Gas Stations'; };
  }, []);

  const cost = parseFloat(costPrice) || 0;
  const sell = parseFloat(sellPrice) || 0;
  const units = parseInt(unitsSold) || 0;

  const profit = sell - cost;
  const marginPercent = sell > 0 ? ((profit / sell) * 100) : 0;
  const markupPercent = cost > 0 ? ((profit / cost) * 100) : 0;
  const totalRevenue = sell * units;
  const totalCost = cost * units;
  const totalProfit = profit * units;
  const annualProfit = period === 'month' ? totalProfit * 12 : period === 'week' ? totalProfit * 52 : totalProfit;

  const marginColor = marginPercent >= 40 ? 'text-emerald-600' : marginPercent >= 20 ? 'text-blue-600' : marginPercent > 0 ? 'text-amber-600' : 'text-red-600';

  // Common smoke shop margins
  const BENCHMARKS = [
    { product: 'Cigarettes', margin: '10-15%', markup: '12-18%' },
    { product: 'Premium Cigars', margin: '35-50%', markup: '55-100%' },
    { product: 'Vapes/E-liquid', margin: '40-60%', markup: '65-150%' },
    { product: 'Rolling Papers', margin: '50-65%', markup: '100-185%' },
    { product: 'Lighters/Accessories', margin: '50-70%', markup: '100-230%' },
    { product: 'CBD Products', margin: '50-70%', markup: '100-230%' },
    { product: 'Glass Pipes', margin: '60-80%', markup: '150-400%' },
    { product: 'Snacks/Drinks', margin: '25-40%', markup: '33-65%' },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      {/* Nav */}
      <nav className="border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-950/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-emerald-600 flex items-center justify-center">
              <Package className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-lg text-slate-900 dark:text-white">FlowFeed</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/tools/tobacco-tax-calculator" className="text-sm text-slate-600 hover:text-emerald-600 hidden md:block">Tax Calculator</Link>
            <Link to="/signup">
              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white">Start Free Trial</Button>
            </Link>
          </div>
        </div>
      </nav>

      <section className="py-12 md:py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-50 dark:bg-blue-950 px-4 py-1.5 text-sm font-medium text-blue-700 dark:text-blue-400 mb-4">
              <Percent className="h-4 w-4" /> Free Tool
            </motion.div>
            <h1 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
              Smoke Shop Profit Margin Calculator
            </h1>
            <p className="text-lg text-slate-600 dark:text-slate-400">
              Calculate profit margins, markup, and monthly earnings for your smoke shop or convenience store products.
            </p>
          </div>

          {/* Calculator */}
          <div className="max-w-2xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl p-6 md:p-8 shadow-xl"
              data-testid="margin-calculator">

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Cost Price ($)</label>
                  <input type="number" value={costPrice} onChange={(e) => setCostPrice(e.target.value)}
                    placeholder="0.00" min="0" step="0.01"
                    className="w-full h-12 px-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-lg"
                    data-testid="cost-input" />
                  <p className="text-xs text-slate-500 mt-1">What you pay wholesale</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Selling Price ($)</label>
                  <input type="number" value={sellPrice} onChange={(e) => setSellPrice(e.target.value)}
                    placeholder="0.00" min="0" step="0.01"
                    className="w-full h-12 px-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-lg"
                    data-testid="sell-input" />
                  <p className="text-xs text-slate-500 mt-1">What customer pays</p>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Units Sold</label>
                <input type="number" value={unitsSold} onChange={(e) => setUnitsSold(e.target.value)}
                  placeholder="0" min="0"
                  className="w-full h-12 px-4 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-lg"
                  data-testid="units-input" />
                <div className="flex gap-2 mt-2">
                  {[{v:'week',l:'Per Week'},{v:'month',l:'Per Month'},{v:'year',l:'Per Year'}].map(p => (
                    <button key={p.v} onClick={() => setPeriod(p.v)}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                        period === p.v ? 'bg-blue-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                      }`}>{p.l}</button>
                  ))}
                </div>
              </div>

              {/* Results */}
              {sell > 0 && cost > 0 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  className="border-t border-slate-200 dark:border-slate-700 pt-6 space-y-4" data-testid="margin-results">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 text-center">
                      <p className="text-xs text-slate-500 mb-1">Profit Margin</p>
                      <p className={`text-3xl font-bold ${marginColor}`}>{marginPercent.toFixed(1)}%</p>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-4 text-center">
                      <p className="text-xs text-slate-500 mb-1">Markup</p>
                      <p className="text-3xl font-bold text-blue-600">{markupPercent.toFixed(1)}%</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-3 text-center">
                      <p className="text-xs text-slate-500 mb-1">Profit/Unit</p>
                      <p className="text-lg font-bold text-emerald-600">${profit.toFixed(2)}</p>
                    </div>
                    <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-3 text-center">
                      <p className="text-xs text-slate-500 mb-1">{period === 'week' ? 'Weekly' : period === 'month' ? 'Monthly' : 'Annual'} Profit</p>
                      <p className="text-lg font-bold text-emerald-600">${totalProfit.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
                    </div>
                    <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-3 text-center">
                      <p className="text-xs text-slate-500 mb-1">Annual Est.</p>
                      <p className="text-lg font-bold text-emerald-600">${annualProfit.toLocaleString(undefined, {maximumFractionDigits: 0})}</p>
                    </div>
                  </div>
                  <div className={`p-3 rounded-lg text-center text-sm font-medium ${
                    marginPercent >= 40 ? 'bg-emerald-100 text-emerald-700' :
                    marginPercent >= 20 ? 'bg-blue-100 text-blue-700' :
                    marginPercent > 10 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {marginPercent >= 40 ? 'Excellent margin! Well above industry average.' :
                     marginPercent >= 20 ? 'Good margin. Competitive pricing.' :
                     marginPercent > 10 ? 'Low margin. Consider adjusting pricing.' :
                     'Very thin margin. Review your costs and pricing.'}
                  </div>
                </motion.div>
              )}
            </motion.div>

            {/* CTA */}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
              className="mt-8 text-center bg-gradient-to-r from-blue-600 to-emerald-600 rounded-2xl p-6 md:p-8 text-white">
              <h2 className="text-xl md:text-2xl font-bold mb-2">Track All Product Margins Automatically</h2>
              <p className="text-blue-100 mb-4 text-sm md:text-base">
                FlowFeed POS calculates margins in real-time for every product, alerts you on low-margin items, and shows your most profitable categories.
              </p>
              <Link to="/signup">
                <Button size="lg" className="bg-white text-blue-700 hover:bg-blue-50 font-semibold px-6">
                  Start Free 14-Day Trial <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <div className="flex items-center justify-center gap-4 mt-4 text-xs text-blue-200">
                <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> No credit card</span>
                <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Real-time margins</span>
                <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Profit reports</span>
              </div>
            </motion.div>
          </div>

          {/* Industry Benchmarks */}
          <div className="max-w-3xl mx-auto mt-16">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6 text-center">
              Average Smoke Shop Profit Margins by Product (2025)
            </h2>
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
                    <th className="px-4 py-3 text-left font-medium text-slate-600 dark:text-slate-400">Product Category</th>
                    <th className="px-4 py-3 text-center font-medium text-slate-600 dark:text-slate-400">Profit Margin</th>
                    <th className="px-4 py-3 text-center font-medium text-slate-600 dark:text-slate-400">Markup</th>
                  </tr>
                </thead>
                <tbody>
                  {BENCHMARKS.map((b, idx) => (
                    <tr key={idx} className={`border-b border-slate-100 dark:border-slate-800 ${idx % 2 === 0 ? '' : 'bg-slate-50/50 dark:bg-slate-800/30'}`}>
                      <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{b.product}</td>
                      <td className="px-4 py-3 text-center text-slate-700 dark:text-slate-300">{b.margin}</td>
                      <td className="px-4 py-3 text-center text-slate-700 dark:text-slate-300">{b.markup}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 text-center mt-3">
              Based on industry surveys and retailer data. Actual margins vary by location, supplier, and volume.
            </p>
          </div>

          {/* FAQ */}
          <div className="max-w-3xl mx-auto mt-16 mb-12">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6 text-center">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {[
                { q: "What is a good profit margin for a smoke shop?", a: "Most successful smoke shops maintain an overall blended margin of 30-45%. Cigarettes have thin margins (10-15%) but high volume, while accessories like glass pipes and vapes have 50-70% margins. The key is balancing high-volume low-margin staples with high-margin accessories." },
                { q: "What's the difference between margin and markup?", a: "Profit margin is profit as a percentage of selling price (e.g., sell for $10, cost $6 = 40% margin). Markup is profit as a percentage of cost (same example = 66.7% markup). Margin is always lower than markup for the same product." },
                { q: "How do I increase my smoke shop profit margins?", a: "Key strategies: 1) Negotiate better wholesale pricing with volume discounts, 2) Add high-margin accessories (glass, rolling papers, lighters), 3) Create bundle deals that increase average ticket size, 4) Reduce shrinkage with inventory tracking, 5) Use a POS system to identify your best and worst margins." },
                { q: "Why are cigarette margins so low?", a: "Cigarette margins are compressed by state excise taxes (which can be $1-5 per pack), minimum price laws in some states, and competitive pressure from nearby stores. However, cigarettes drive foot traffic — the real profit comes from add-on purchases customers make." },
              ].map((faq, idx) => (
                <details key={idx} className="bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 group">
                  <summary className="px-5 py-4 cursor-pointer font-medium text-slate-900 dark:text-white list-none flex items-center justify-between">
                    {faq.q}
                    <span className="text-slate-400 group-open:rotate-45 transition-transform text-xl">+</span>
                  </summary>
                  <p className="px-5 pb-4 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 dark:border-slate-800 py-6">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-slate-500">&copy; 2025 FlowFeed. Free profit margin calculator for US retailers.</p>
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <Link to="/" className="hover:text-emerald-600">Home</Link>
            <Link to="/tools/tobacco-tax-calculator" className="hover:text-emerald-600">Tax Calculator</Link>
            <Link to="/signup" className="hover:text-emerald-600">Sign Up</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
